// =============================================================================
// io/sync/saveRecordings.ts — the voice-note list on a journal day.
// -----------------------------------------------------------------------------
// ONE column: `recordings`. A separate module rather than an argument on the
// recap write, for the reason D62 gives — a writer owns a fixed column set, and
// widening `saveJournalRecap` to carry recordings would make the recap form the
// owner of a list it does not render.
//
// The old client has no equivalent: it autosaves the entire journal entry on
// every change, so a voice note rides the same write as the prose. This client
// cannot do that (the typed model is lossy — see saveJournalRecap's header), so
// each owned thing gets its own narrow statement.
//
// ── 🚨 THE LIST IS SENT WHOLE, WHICH IS ONLY SAFE BECAUSE IT IS READ WHOLE ──
//
// `loadJournal` reads `recordings` in full and filters it to strings; the UI
// renders every entry it gets. So the array in memory IS the array on disk, and
// sending it back cannot drop an element the client never saw. If that read ever
// becomes partial — a limit, a projection — this write becomes a truncation and
// must change with it. D62, one column over.
//
// ⚠ THIS IS THE ONE PLACE A RECORDING CAN BE LOST, and the loss is authorised:
// removing the last reference to an object leaves the object in the bucket
// (there is no backup, so nothing here deletes) but nothing points at it any
// more. The UI asks before it does that.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';

/** Every column this module writes. Fixed, and asserted by a test. */
export const RECORDINGS_COLUMNS = ['user_id', 'date', 'recordings'] as const;

export interface SaveRecordingsArgs {
  sb: SupabaseClient;
  userId: string;
  date: string;
  recordings: readonly string[];
  baselineUpdatedAt: string | null;
}

export interface SaveRecordingsResult {
  updatedAt: string | null;
  error: string | null;
  heldStale: boolean;
}

export async function saveRecordings(
  args: SaveRecordingsArgs,
): Promise<SaveRecordingsResult> {
  const { sb, userId, date, recordings, baselineUpdatedAt } = args;

  // ⚠ FAIL-OPEN, identical to the other two journal writers. A guard that
  // blocked a save it could not verify would lose real work on a transient blip.
  if (baselineUpdatedAt) {
    const { data, error } = await sb
      .from('journal_entries')
      .select('date, updated_at')
      .eq('user_id', userId)
      .eq('date', date);

    if (!error && Array.isArray(data) && data.length === 1) {
      const cloud = (data[0] as unknown as Record<string, unknown>)['updated_at'];
      if (typeof cloud === 'string'
        && new Date(cloud).getTime() > new Date(baselineUpdatedAt).getTime()) {
        return {
          updatedAt: null,
          heldStale: true,
          error:
            `This day was changed somewhere else after you loaded it — probably the ` +
            `other client or another tab. Nothing was saved, so neither version is ` +
            `lost. Reload to see the newer copy, then re-apply your edit.`,
        };
      }
    }
  }

  const { data, error } = await sb
    .from('journal_entries')
    .upsert({ user_id: userId, date, recordings: [...recordings] }, { onConflict: 'user_id,date' })
    .select('date, updated_at');

  if (error) {
    return {
      updatedAt: null,
      heldStale: false,
      error: reportFailureAs(
        'save recordings', error, `Save failed: ${error.message}`,
      ),
    };
  }
  if (!Array.isArray(data) || data.length !== 1) {
    return {
      updatedAt: null,
      heldStale: false,
      error:
        `The save returned ${Array.isArray(data) ? data.length : 'no'} rows instead of one, ` +
        `so it cannot be confirmed. Reload before editing again.`,
    };
  }

  const ts = (data[0] as unknown as Record<string, unknown>)['updated_at'];
  return { updatedAt: typeof ts === 'string' ? ts : null, heldStale: false, error: null };
}
